// Drag handle for a resizable layout column (`PlatformWeb.ResizeHandle`). Sets the column's CSS
// variable on <html> and saves the width through `layout_resized`.
import { updateLayout } from '../layout';

const SAVE_DELAY_MS = 400;
// How far the grip's centre stays from the edge's ends, clear of the page card's rounded corners.
const GRIP_INSET = 44;

export default {
  ResizeHandle: {
    mounted() {
      this.root = document.documentElement;
      this.column = this.el.closest(this.el.dataset.target);
      this.cssVar = this.el.dataset.cssVar;
      this.key = this.el.dataset.key;
      this.min = Number(this.el.dataset.min);
      this.max = Number(this.el.dataset.max);
      this.defaultWidth = Number(this.el.dataset.defaultWidth);
      // A handle on the left edge (a column docked on the right) grows the column leftwards.
      this.direction = this.el.dataset.edge === 'left' ? -1 : 1;

      this.onPointerDown = e => this.startDrag(e);
      this.onDoubleClick = () => this.save(this.apply(this.defaultWidth));
      // The grip follows the pointer along the edge, and stays where it was grabbed while dragging.
      this.onHover = e => this.endDrag || this.moveGrip(e.clientY);

      this.el.addEventListener('pointerdown', this.onPointerDown);
      this.el.addEventListener('dblclick', this.onDoubleClick);
      this.el.addEventListener('pointermove', this.onHover);
    },

    destroyed() {
      clearTimeout(this.saveTimer);
      this.endDrag?.();
    },

    moveGrip(clientY) {
      const { top, height } = this.el.getBoundingClientRect();
      const y = Math.min(Math.max(clientY - top, GRIP_INSET), height - GRIP_INSET);
      this.el.style.setProperty('--grip-y', `${y}px`);
    },

    currentWidth() {
      return Math.round(this.column.getBoundingClientRect().width);
    },

    apply(width) {
      const clamped = Math.round(Math.min(this.max, Math.max(this.min, width)));
      this.root.style.setProperty(this.cssVar, `${clamped}px`);
      updateLayout();
      return clamped;
    },

    save(width) {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.pushEvent('layout_resized', { key: this.key, width }), SAVE_DELAY_MS);
    },

    startDrag(e) {
      if (e.button !== 0) return;
      e.preventDefault();
      this.moveGrip(e.clientY);

      const startX = e.clientX;
      const startWidth = this.currentWidth();
      let width = startWidth;

      this.el.setPointerCapture(e.pointerId);
      // Sizing the sidebar is choosing it: don't let it then collapse on its own for room.
      if (this.key === 'sidebar_width') this.root.setAttribute('data-sidebar-pinned', '');
      this.el.dataset.resizing = '';
      this.root.setAttribute('data-resizing', '');
      this.root.setAttribute('data-instant', '');

      const onMove = moveEvent => {
        width = this.apply(startWidth + (moveEvent.clientX - startX) * this.direction);
      };

      const onUp = () => {
        this.endDrag();
        if (width !== startWidth) this.save(width);
      };

      this.endDrag = () => {
        this.el.removeEventListener('pointermove', onMove);
        this.el.removeEventListener('pointerup', onUp);
        this.el.removeEventListener('pointercancel', onUp);
        delete this.el.dataset.resizing;
        this.root.removeAttribute('data-resizing');
        this.root.removeAttribute('data-instant');
        this.endDrag = null;
      };

      this.el.addEventListener('pointermove', onMove);
      this.el.addEventListener('pointerup', onUp);
      this.el.addEventListener('pointercancel', onUp);
    },
  },
};
